import { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import VehicleForm from "./_components/VehicleForm";
import {
  extractFleetMediaFilename,
  getFleetUploadRelativeUrl,
} from "../../../../api/fleet-management/api";
import {
  useDeleteMediaMutation,
  useUploadMediaMutation,
} from "../../../../api/fleet-management/media/media.hooks";
import {
  useCreateVehicleMutation,
  useGetVehicleById,
  useUpdateVehicleMutation,
} from "../../../../api/fleet-management/vehicles/vehicles.hooks";
import {
  buildVehiclePayload,
  initialVehicleForm,
  mapVehicleToForm,
  validateVehicleForm,
} from "./utils/vehicle.utils";

const VehicleFormPage = () => {
  const history = useHistory();
  const { vehicleId } = useParams();
  const isEditMode = Boolean(vehicleId);

  const [form, setForm] = useState(initialVehicleForm);
  const [initialFormState, setInitialFormState] = useState(initialVehicleForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [mediaFeedback, setMediaFeedback] = useState({ type: "", message: "" });
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);

  const {
    data: vehicleData,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
    error: vehicleError,
  } = useGetVehicleById(vehicleId);

  const {
    mutate: createVehicle,
    isLoading,
    isPending,
    error,
    reset,
  } = useCreateVehicleMutation();

  const {
    mutate: updateVehicle,
    isLoading: isUpdating,
    isPending: isUpdatePending,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateVehicleMutation();

  const {
    mutateAsync: uploadMedia,
    isLoading: isUploadingMedia,
  } = useUploadMediaMutation();

  const {
    mutateAsync: deleteMedia,
    isLoading: isDeletingMedia,
  } = useDeleteMediaMutation();

  useEffect(() => {
    if (!isEditMode || !vehicleData) return;
    const nextForm = mapVehicleToForm(vehicleData);
    setForm(nextForm);
    setInitialFormState(nextForm);
  }, [vehicleData, isEditMode]);

  const submitting = isLoading || isPending || isUpdating || isUpdatePending;
  const submitError = isEditMode ? updateError : error;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const updateSpecifications = (key, value) => {
    setForm((prev) => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [key]: value,
      },
    }));
    setValidationErrors((prev) => ({ ...prev, [`specifications.${key}`]: undefined }));
  };

  const toggleSpecification = (key, value) => {
    setForm((prev) => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [key]: Boolean(value),
      },
    }));
  };

  const updateImage = (index, key, value) => {
    setForm((prev) => {
      const nextImages = [...prev.images];
      nextImages[index] = {
        ...nextImages[index],
        [key]: value,
      };
      return { ...prev, images: nextImages };
    });
    setValidationErrors((prev) => ({ ...prev, [`images.${index}.type`]: undefined }));
  };

  const addImage = () => {
    setForm((prev) => ({
      ...prev,
      images: [...prev.images, { url: "", type: "exterior" }],
    }));
  };

  const removeImage = (index) => {
    const imageUrl = form.images[index]?.url;
    const filename = extractFleetMediaFilename(imageUrl);

    const removeFromState = () => {
      setForm((prev) => ({
        ...prev,
        images: prev.images.filter((_, imageIndex) => imageIndex !== index),
      }));
    };

    if (!filename) {
      removeFromState();
      return;
    }

    deleteMedia({ filename })
      .then(() => {
        removeFromState();
      })
      .catch(() => {
        removeFromState();
      });
  };

  const handleUploadImage = async (index, file) => {
    if (!file) return;

    setMediaFeedback({ type: "", message: "" });
    setUploadingImageIndex(index);

    try {
      const uploadResult = await uploadMedia(file);
      const relativePath = getFleetUploadRelativeUrl(uploadResult, 0);

      if (!relativePath) {
        throw new Error("Upload response did not include media path.");
      }

      // Store relative URL in form state (e.g., /uploads/filename.png)
      // The utility function will convert to full URL when displaying
      updateImage(index, "url", relativePath);
      setMediaFeedback({ type: "success", message: "Image uploaded successfully." });
    } catch (uploadError) {
      setMediaFeedback({
        type: "danger",
        message: uploadError?.response?.data?.message || "Failed to upload image.",
      });
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const updateFeature = (index, value) => {
    setForm((prev) => {
      const nextFeatures = [...prev.features];
      nextFeatures[index] = value;
      return { ...prev, features: nextFeatures };
    });
  };

  const addFeature = () => {
    setForm((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const removeFeature = (index) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, featureIndex) => featureIndex !== index),
    }));
  };

  const validate = () => {
    const nextErrors = validateVehicleForm(form);
    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    reset();
    resetUpdate();

    if (!validate()) return;

    const payload = buildVehiclePayload(form);

    if (isEditMode) {
      updateVehicle(
        { vehicleId, vehicleData: payload },
        {
          onSuccess: () => {
            setSuccessMessage("Vehicle updated successfully.");
            setInitialFormState(mapVehicleToForm(payload));
          },
        }
      );
      return;
    }

    createVehicle(payload, {
      onSuccess: () => {
        setSuccessMessage("Vehicle created successfully.");
        setForm(initialVehicleForm);
      },
    });
  };

  if (isEditMode && isVehicleLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && isVehicleError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {vehicleError?.response?.data?.message || "Failed to load vehicle details."}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => history.push("/fleet-management/vehicles")}
              aria-label="Back to vehicles list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">{isEditMode ? "Edit Vehicle" : "Create Vehicle"}</h4>
          </div>
          <span className="badge bg-info text-dark">Fleet Management</span>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            {isEditMode
              ? "Update vehicle details and specifications."
              : "Fill in vehicle details and specifications."}
          </p>

          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError?.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} vehicle. Please try again.`}
            </Alert>
          )}

          {mediaFeedback.message && (
            <Alert variant={mediaFeedback.type} className="mb-4">
              {mediaFeedback.message}
            </Alert>
          )}

          <VehicleForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            isEditMode={isEditMode}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onSpecificationsChange={updateSpecifications}
            onToggleSpecification={toggleSpecification}
            onImageChange={updateImage}
            onUploadImage={handleUploadImage}
            onAddImage={addImage}
            onRemoveImage={removeImage}
            onFeatureChange={updateFeature}
            onAddFeature={addFeature}
            onRemoveFeature={removeFeature}
            uploadingImageIndex={uploadingImageIndex}
            isDeletingMedia={isDeletingMedia || isUploadingMedia}
            onReset={() => {
              setForm(isEditMode ? initialFormState : initialVehicleForm);
              setValidationErrors({});
              setSuccessMessage("");
              setMediaFeedback({ type: "", message: "" });
              reset();
              resetUpdate();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default VehicleFormPage;
